import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import hotelService from '../services/hotelService';
import { formatDate } from '../utils/helpers';
import { getSlugFromParams } from '../utils/urlHelpers';
import { joinLocationQuery, resolveGoogleMapsHref } from '../utils/googleMaps';
import { StarIcon } from '@heroicons/react/24/solid';
import useImageLightbox from '../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../utils/imageLightbox';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Component to handle map resize
function MapResizeHandler() {
  const map = useMap();
  
  useEffect(() => {
    // Invalidate size when component mounts
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }, [map]);
  
  return null;
}

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const HotelDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const hotelSlug = getSlugFromParams(params, 'hotel');
  const [hotel, setHotel] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedHotels, setRelatedHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const { openLightbox, Lightbox } = useImageLightbox();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [distance, setDistance] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationName, setLocationName] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (hotelSlug) {
      loadHotel();
    }
    // Get user location
    getUserLocation();
  }, [hotelSlug]);

  const getUserLocation = async () => {
    try {
      const location = await hotelService.getUserLocation();
      setUserLocation(location);
    } catch (err) {
      console.log('Location access denied or unavailable:', err);
      setLocationError('Location access denied');
    }
  };

  useEffect(() => {
    if (hotel?.id) {
      loadReviews();
      loadRelatedHotels();
    }
  }, [hotel?.id]);

  // Reload hotel when user location is available to get distance
  useEffect(() => {
    if (userLocation && hotelSlug && !loading) {
      loadHotel();
    }
  }, [userLocation]);

  const loadHotel = async () => {
    try {
      setLoading(true);
      setError(null);
      // Include user location in params if available
      const params = {};
      if (userLocation) {
        params.user_latitude = userLocation.latitude;
        params.user_longitude = userLocation.longitude;
      }
      const hotelData = await hotelService.getHotel(hotelSlug, params);
      console.log('Hotel data loaded:', hotelData);
      console.log('Hotel latitude:', hotelData.latitude, 'Type:', typeof hotelData.latitude);
      console.log('Hotel longitude:', hotelData.longitude, 'Type:', typeof hotelData.longitude);
      console.log('Has coordinates:', hotelData.latitude != null && hotelData.longitude != null);
      setHotel(hotelData);
      setViews(hotelData.views || 0);
      
      // Set distance if available (from backend - should be road distance if API key is set)
      if (hotelData.distance !== null && hotelData.distance !== undefined) {
        setDistance(hotelData.distance);
      } else if (hotelData.latitude && hotelData.longitude) {
        // If road distance unavailable, get location name from coordinates
        try {
          const locationData = await hotelService.reverseGeocode(
            parseFloat(hotelData.latitude),
            parseFloat(hotelData.longitude)
          );
          if (locationData && locationData.location_name) {
            setLocationName(locationData.location_name);
          }
        } catch (error) {
          console.log('Reverse geocoding failed:', error);
        }
      }
    } catch (err) {
      console.error('Error loading hotel:', err);
      setError('Failed to load hotel details');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!hotel?.slug) return;
    try {
      const response = await hotelService.getHotelReviews(hotel.slug);
      setReviews(response.results || response || []);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviews([]);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!hotel?.slug) return;
    
    setSubmittingReview(true);
    try {
      await hotelService.createHotelReview(hotel.slug, reviewForm);
      // Reload reviews and hotel data
      await loadReviews();
      await loadHotel();
      // Reset form
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const loadRelatedHotels = async () => {
    if (!hotel?.id) return;
    
    try {
      const response = await hotelService.getHotels({ 
        accommodation_type: hotel.accommodation_type,
        page_size: 6,
      });
      // Filter out the current hotel
      const filtered = (response.results || response).filter(
        (h) => h.id !== hotel.id && h.slug !== hotel.slug
      );
      setRelatedHotels(filtered.slice(0, 6));
    } catch (err) {
      console.error('Error loading related hotels:', err);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    return img.original_image || img.image || img.thumbnail || null;
  };

  const primaryImage = hotel?.primary_image || hotel?.images?.[0];
  const allImages = hotel?.images || [];
  const currentImage = allImages[currentImageIndex] || primaryImage;

  // Format price
  const formatPrice = (price, currency = 'PKR') => {
    if (!price) return 'Contact for price';
    const currencySymbol = currency === 'PKR' ? 'PKR' : currency === 'USD' ? '$' : currency;
    return `${currencySymbol} ${parseFloat(price).toLocaleString()}`;
  };

  // Format review count
  const formatReviewCount = (count) => {
    if (!count || count === 0) return '0';
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  // Format distance
  const formatDistance = (distance) => {
    if (!distance) return null;
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m away`;
    }
    return `${distance.toFixed(1)}km away`;
  };

  // Render stars
  const renderStars = (stars) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={`h-5 w-5 ${
          index < stars ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const canManage = user && hotel && canManageOwnedResource(user, hotel);

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading hotel details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Hotel Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/points')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Hotels
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!hotel) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">🏨</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Hotel Not Found</h2>
            <p className="text-gray-600 mb-6">The hotel you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/points')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Hotels
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const hotelMapsCoordHref = resolveGoogleMapsHref({
    latitude: hotel.latitude,
    longitude: hotel.longitude,
  });
  const hotelMapsAddressHref = resolveGoogleMapsHref({
    query: joinLocationQuery([hotel.city, hotel.address]),
  });

  return (
    <PageLayout title={`${hotel.name} - PakPoints`}>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Hotel Images - Main Display */}
                <div className="relative">
                  <div className="aspect-w-16 aspect-h-9">
                    <div className="w-full h-96 bg-gray-200 flex items-center justify-center">
                      {currentImage && getImageUrl(currentImage) ? (
                        <button
                          type="button"
                          onClick={() =>
                            openLightbox(
                              normalizeGalleryImages(allImages, hotel?.name || 'Hotel'),
                              currentImageIndex,
                            )
                          }
                          className="w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                          aria-label="View full size image"
                        >
                          <img
                            src={getImageUrl(currentImage)}
                            alt={hotel.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ) : (
                        <div className="text-center text-gray-400">
                          <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <p>No image available</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image Navigation Arrows */}
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1))}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition-colors"
                        aria-label="Previous image"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0))}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 hover:bg-white transition-colors"
                        aria-label="Next image"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}

                  {/* Image Dots Indicator */}
                  {allImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                      {allImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentImageIndex ? 'bg-white w-8' : 'bg-white/60'
                          }`}
                          aria-label={`Go to image ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Image Gallery */}
                {allImages.length > 0 && (
                  <div className="p-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Image Gallery</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {allImages.map((image, index) => (
                        <div
                          key={image.id || index}
                          className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex ? 'border-blue-500' : 'border-gray-200 hover:border-blue-500'
                          }`}
                          onClick={() => {
                            setCurrentImageIndex(index);
                            openLightbox(
                              normalizeGalleryImages(allImages, hotel?.name || 'Hotel'),
                              index,
                            );
                          }}
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`${hotel.name} - Image ${index + 1}`}
                            className="w-full h-24 object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Click to view
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {allImages.length} image{allImages.length !== 1 ? 's' : ''} total
                    </p>
                  </div>
                )}

                {/* Hotel Content */}
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">{hotel.name}</h1>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {views} views
                        </span>
                        <span>•</span>
                        <span>{formatDate(hotel.created_at)}</span>
                        <span>•</span>
                        <span className="capitalize">{hotel.accommodation_type?.replace(/_/g, ' ')}</span>
                        {distance && (
                          <>
                            <span>•</span>
                            <span className="flex items-center text-blue-600 font-semibold">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {formatDistance(distance)}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {renderStars(hotel.stars || 0)}
                        {parseFloat(hotel.average_rating || 0) > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {typeof hotel.average_rating === 'number' 
                                ? hotel.average_rating.toFixed(1) 
                                : parseFloat(hotel.average_rating || 0).toFixed(1)}
                            </span>
                            <span className="text-blue-600">
                              ({formatReviewCount(hotel.total_reviews)})
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-blue-600">{hotel.society?.name || hotel.city || hotel.area}</div>
                      <div className="text-sm text-gray-500">Location</div>
                    </div>
                  </div>

                  {/* Owner Actions */}
                  {canManage && (
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => navigate(`/points/${hotel.slug}/edit`)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Hotel
                      </button>
                    </div>
                  )}

                  {/* Hotel Details */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Hotel Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Accommodation Type</p>
                        <p className="font-medium capitalize">{hotel.accommodation_type?.replace(/_/g, ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Star Rating</p>
                        <div className="flex items-center gap-1">
                          {renderStars(hotel.stars || 0)}
                          <span className="ml-2 text-gray-700">{hotel.stars || 0} stars</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Price per Night</p>
                        <p className="font-medium text-green-600">{formatPrice(hotel.price_per_night, hotel.currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Contact Phone</p>
                        {hotel.contact_phone ? (
                          <a
                            href={`tel:${hotel.contact_phone}`}
                            className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {hotel.contact_phone}
                          </a>
                        ) : (
                          <p className="font-medium text-gray-500">Not provided</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Contact Email</p>
                        {hotel.contact_email ? (
                          <a
                            href={`mailto:${hotel.contact_email}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {hotel.contact_email}
                          </a>
                        ) : (
                          <p className="font-medium text-gray-500">Not provided</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Website</p>
                        {hotel.website ? (
                          <a
                            href={hotel.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Visit Website
                          </a>
                        ) : (
                          <p className="font-medium text-gray-500">Not provided</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Address</p>
                        <p className="font-medium">{hotel.address || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">City/Area</p>
                        <p className="font-medium">{hotel.city || hotel.area || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Amenities</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {hotel.parking_available && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                          </svg>
                          <span className="text-sm">Free Parking</span>
                        </div>
                      )}
                      {hotel.wifi_available && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                          </svg>
                          <span className="text-sm">Free Wi-Fi</span>
                        </div>
                      )}
                      {hotel.restaurant && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                          </svg>
                          <span className="text-sm">Restaurant</span>
                        </div>
                      )}
                      {hotel.swimming_pool && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            <ellipse cx="12" cy="12" rx="6" ry="4"/>
                          </svg>
                          <span className="text-sm">Swimming Pool</span>
                        </div>
                      )}
                      {hotel.gym && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <span className="text-sm">Gym</span>
                        </div>
                      )}
                      {hotel.spa && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="text-sm">Spa</span>
                        </div>
                      )}
                      {hotel.family_friendly && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-sm">Family Friendly</span>
                        </div>
                      )}
                      {hotel.pet_friendly && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          <span className="text-sm">Pet Friendly</span>
                        </div>
                      )}
                      {hotel.breakfast_included && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 3H4v10c0 2.21 1.79 4 4 4h6c2.21 0 4-1.79 4-4v-2h2c1.11 0 2-.9 2-2V5c0-1.11-.89-2-2-2zm0 4h-2V5h2v2zM4 19h16v2H4z"/>
                          </svg>
                          <span className="text-sm">Free Breakfast</span>
                        </div>
                      )}
                      {hotel.free_cancellation && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm">Free Cancellation</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{hotel.description || 'No description available'}</p>
                  </div>

                  {/* Nearby Places */}
                  {hotel.nearby_places_list && hotel.nearby_places_list.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Nearby Places</h3>
                      <div className="flex flex-wrap gap-2">
                        {hotel.nearby_places_list.map((place, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {place}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Location Map */}
                  {(() => {
                    const lat = hotel.latitude;
                    const lon = hotel.longitude;
                    // Check if coordinates exist and are valid numbers
                    const latNum = lat != null && lat !== '' ? parseFloat(lat) : null;
                    const lonNum = lon != null && lon !== '' ? parseFloat(lon) : null;
                    const hasValidCoords = latNum != null && lonNum != null && 
                                         !isNaN(latNum) && !isNaN(lonNum) &&
                                         latNum !== 0 && lonNum !== 0;
                    console.log('Map condition check:', { 
                      lat, 
                      lon, 
                      latNum, 
                      lonNum, 
                      hasValidCoords,
                      latType: typeof lat, 
                      lonType: typeof lon 
                    });
                    return hasValidCoords;
                  })() ? (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                        {distance ? (
                          <div className="flex items-center gap-2 text-blue-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-semibold">{formatDistance(distance)}</span>
                          </div>
                        ) : (locationName || hotel?.area || hotel?.city) && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-semibold">In {locationName || hotel?.area || hotel?.city}</span>
                          </div>
                        )}
                      </div>
                      <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 relative" style={{ minHeight: '384px' }}>
                        <MapContainer
                          key={`${hotel.latitude}-${hotel.longitude}`}
                          center={[parseFloat(hotel.latitude), parseFloat(hotel.longitude)]}
                          zoom={14}
                          style={{ height: '100%', width: '100%', minHeight: '384px' }}
                          scrollWheelZoom={true}
                          whenReady={(map) => {
                            setTimeout(() => {
                              if (map && map.target) {
                                map.target.invalidateSize();
                              }
                            }, 100);
                          }}
                        >
                          <MapResizeHandler />
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            maxZoom={19}
                            maxNativeZoom={19}
                          />
                          <Marker position={[parseFloat(hotel.latitude), parseFloat(hotel.longitude)]}>
                            <Popup>
                              <div className="text-center">
                                <strong>{hotel.name}</strong>
                                {hotel.address && <p className="text-sm text-gray-600 mt-1">{hotel.address}</p>}
                                {(hotel.area || hotel.city) && !hotel.address && (
                                  <p className="text-sm text-gray-600 mt-1">{hotel.area || hotel.city}</p>
                                )}
                                {hotelMapsCoordHref && (
                                  <a
                                    href={hotelMapsCoordHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-xs mt-2 inline-block"
                                  >
                                    Open in Google Maps
                                  </a>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        {hotelMapsCoordHref && (
                          <a
                            href={hotelMapsCoordHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Open in Google Maps
                          </a>
                        )}
                        {!userLocation && !locationError && (
                          <button
                            onClick={getUserLocation}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Show distance from my location
                          </button>
                        )}
                        {locationError && (
                          <span className="text-sm text-gray-500">Location access denied</span>
                        )}
                      </div>
                    </div>
                  ) : hotel.city ? (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                      </div>
                      <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 relative" style={{ minHeight: '384px' }}>
                        <MapContainer
                          key={`city-${hotel.city}`}
                          center={[34.0479, 73.3584]} // Default center (Pakistan coordinates)
                          zoom={10}
                          style={{ height: '100%', width: '100%', minHeight: '384px' }}
                          scrollWheelZoom={true}
                          whenReady={(map) => {
                            setTimeout(() => {
                              if (map && map.target) {
                                map.target.invalidateSize();
                              }
                            }, 100);
                          }}
                        >
                          <MapResizeHandler />
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            maxZoom={19}
                            maxNativeZoom={19}
                          />
                          <Marker position={[34.0479, 73.3584]}>
                            <Popup>
                              <div className="text-center">
                                <strong>{hotel.name}</strong>
                                {hotel.city && <p className="text-sm text-gray-600 mt-1">{hotel.city}</p>}
                                {hotel.address && <p className="text-sm text-gray-600 mt-1">{hotel.address}</p>}
                                {hotelMapsAddressHref && (
                                  <a
                                    href={hotelMapsAddressHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-xs mt-2 inline-block"
                                  >
                                    Open in Google Maps
                                  </a>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                      <div className="mt-2">
                        {hotelMapsAddressHref && (
                          <a
                            href={hotelMapsAddressHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Open in Google Maps
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Location</h3>
                      <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <p>Location coordinates not available</p>
                          {hotel.address && (
                            <p className="text-sm mt-2">{hotel.address}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rooms Section */}
                  {hotel.rooms && hotel.rooms.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Rooms ({hotel.rooms.length})</h3>
                      <div className="space-y-4">
                        {hotel.rooms.map((room) => (
                          <div key={room.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-1">
                                  {room.name || room.get_room_type_display || room.room_type}
                                </h4>
                                {room.description && (
                                  <p className="text-gray-600 text-sm mb-2">{room.description}</p>
                                )}
                                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                  <span>Max Occupancy: {room.max_occupancy}</span>
                                  <span>Beds: {room.beds}</span>
                                  {room.bed_type && <span>Bed Type: {room.bed_type}</span>}
                                  {room.room_size && <span>Size: {room.room_size} m²</span>}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {room.has_wifi && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Wi-Fi</span>
                                  )}
                                  {room.has_tv && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">TV</span>
                                  )}
                                  {room.has_ac && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">AC</span>
                                  )}
                                  {room.has_kitchen && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Kitchen</span>
                                  )}
                                </div>
                              </div>
                              {room.price_per_night && (
                                <div className="text-right ml-4">
                                  <div className="text-lg font-bold text-green-600">
                                    {formatPrice(room.price_per_night, room.currency)}
                                  </div>
                                  <div className="text-sm text-gray-500">per night</div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reviews Section */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Reviews ({hotel.total_reviews || 0})
                      </h3>
                      {user && (
                        <button
                          onClick={() => setShowReviewForm(!showReviewForm)}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {showReviewForm ? 'Cancel' : 'Write a Review'}
                        </button>
                      )}
                    </div>

                    {/* Review Form */}
                    {showReviewForm && user && (
                      <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <form onSubmit={handleReviewSubmit}>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Rating
                            </label>
                            <div className="flex items-center gap-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                                  className="focus:outline-none"
                                >
                                  <StarIcon
                                    className={`h-6 w-6 ${
                                      star <= reviewForm.rating
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                </button>
                              ))}
                              <span className="ml-2 text-sm text-gray-600">
                                {reviewForm.rating} out of 5
                              </span>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title (optional)
                            </label>
                            <input
                              type="text"
                              value={reviewForm.title}
                              onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Give your review a title"
                            />
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Comment (optional)
                            </label>
                            <textarea
                              value={reviewForm.comment}
                              onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Share your experience..."
                            />
                          </div>

                          <div className="flex justify-end">
                            <button
                              type="submit"
                              disabled={submittingReview}
                              className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {submittingReview ? 'Submitting...' : 'Submit Review'}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {!user && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          Please <a href="/login" className="underline font-medium">login</a> to write a review.
                        </p>
                      </div>
                    )}

                    {reviews.length > 0 ? (
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">{review.user_name || 'Anonymous'}</h4>
                                <div className="flex items-center gap-1 mt-1">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                              <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                            </div>
                            {review.title && (
                              <h5 className="font-medium text-gray-800 mb-1">{review.title}</h5>
                            )}
                            {review.comment && (
                              <p className="text-gray-700 text-sm">{review.comment}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>No reviews yet. Be the first to review this hotel!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Price per Night</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatPrice(hotel.price_per_night, hotel.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Rating</p>
                    <div className="flex items-center gap-2">
                      {renderStars(Math.round(parseFloat(hotel.average_rating || 0)))}
                      <span className="font-semibold">
                        {typeof hotel.average_rating === 'number' 
                          ? hotel.average_rating.toFixed(1) 
                          : parseFloat(hotel.average_rating || 0).toFixed(1)}
                      </span>
                      <span className="text-blue-600 text-sm">({formatReviewCount(hotel.total_reviews)})</span>
                    </div>
                  </div>
                  {distance ? (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Distance</p>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-lg font-bold text-blue-600">{formatDistance(distance)}</span>
                      </div>
                    </div>
                  ) : (locationName || hotel?.area || hotel?.city) && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Location</p>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-lg font-bold text-gray-700">{locationName || hotel?.area || hotel?.city}</span>
                      </div>
                    </div>
                  )}
                  {hotel.contact_phone && (
                    <div>
                      <a
                        href={`tel:${hotel.contact_phone}`}
                        className="block w-full bg-blue-600 text-white text-center py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Call Now
                      </a>
                    </div>
                  )}
                  {hotel.website && (
                    <div>
                      <a
                        href={hotel.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-green-600 text-white text-center py-3 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Related Hotels Section */}
          {relatedHotels.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Hotels</h2>
              <div className="overflow-x-auto">
                <div className="flex space-x-4 pb-4">
                  {relatedHotels.map((relatedHotel) => {
                    const relatedImage = relatedHotel.primary_image || relatedHotel.images?.[0];
                    const relatedImageUrl = getImageUrl(relatedImage);
                    return (
                      <div
                        key={relatedHotel.id}
                        onClick={() => navigate(`/hotels/${relatedHotel.slug}`)}
                        className="flex-shrink-0 w-64 bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                      >
                        <div className="aspect-w-16 aspect-h-9">
                          <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
                            {relatedImageUrl ? (
                              <img
                                src={relatedImageUrl}
                                alt={relatedHotel.name}
                                className="w-full h-full object-cover rounded-t-lg"
                              />
                            ) : (
                              <div className="text-center text-gray-400">
                                <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">
                            {relatedHotel.name}
                          </h3>
                          <div className="flex items-center gap-1 mb-2">
                            {renderStars(relatedHotel.stars || 0)}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 capitalize">
                              {relatedHotel.accommodation_type?.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-blue-600">
                              View Details
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {Lightbox}
    </PageLayout>
  );
};

export default HotelDetailPage;

